import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TablePagination,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { apiClient } from '../api/apiClient';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const DEBUG = true;

const SACHGEBIETE = [
  { code: 'KR', name: 'Krankenversicherung' },
  { code: 'R', name: 'Rentenversicherung' },
  { code: 'U', name: 'Unfallversicherung' },
  { code: 'AL', name: 'Arbeitslosenversicherung' },
  { code: 'P', name: 'Pflegeversicherung' },
  { code: 'SB', name: 'Schwerbehindertenrecht' },
  { code: 'SO', name: 'Sozialhilfe' },
  { code: 'AS', name: 'Grundsicherung für Arbeitsuchende' },
  { code: 'KA', name: 'Vertragsarztangelegenheiten' },
  { code: 'KG', name: 'Kindergeld-/Erziehungsgeldangelegenheiten' },
  { code: 'VS', name: 'Entschädigungs-/Schwerbehindertenrecht' },
  { code: 'Z', name: 'Sonstige Angelegenheiten' },
  { code: 'ÜG', name: 'Entschädigungsklage bei überlanger Verfahrensdauer' },
  { code: 'BA', name: 'Betriebsprüfungen' },
  { code: 'AY', name: 'Asylbewerberleistungsgesetz' },
  { code: 'KS', name: 'Künstlersozialversicherung' },
  { code: 'LW', name: 'Alterssicherung der Landwirte' }
];

const KATEGORIEN = [
  { code: 'B', name: 'Beschluss' },
  { code: 'G', name: 'Gerichtsbescheid' },
  { code: 'T', name: 'Teilurteil' },
  { code: 'U', name: 'Urteil' },
  { code: 'K', name: 'Kostenbeschluss' }
];

const LIMIT_OPTIONS = [10, 25, 50, 100];

// Instanz-Definitionen
const INSTANZ_COLORS = {
  1: '#4caf50', // Grün für erste Instanz (SG)
  2: '#2196f3', // Blau für zweite Instanz (LSG)
  3: '#9c27b0'  // Violett für dritte Instanz (BSG)
};

const INSTANZ_LABELS = {
  1: 'I',
  2: 'II',
  3: 'III'
};

const debug = (message, data = null) => {
  if (!DEBUG) return;
  const stack = new Error().stack;
  const caller = stack.split('\n')[2].trim();
  const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
  console.log(`[${timestamp}][Entscheidungen] ${message}`, data ? data : '');
  console.log('Caller:', caller);
};

const Entscheidungen = () => {
  debug('Component wird initialisiert');
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State für die Erkennung von Browser-Navigation
  const isNavigatingRef = useRef(false);
  
  const [entscheidungen, setEntscheidungen] = useState([]);
  const [gerichte, setGerichte] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState('datum_1_instanz');
  const [order, setOrder] = useState('desc');
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const initialLoadDoneRef = useRef(false);

  // Ersetze die lokalen Konstanten durch die globalen
  const sachgebiete = SACHGEBIETE;
  const kategorien = KATEGORIEN;
  const limitOptions = LIMIT_OPTIONS;

  const [filter, setFilter] = useState({
    sachgebiet: '',
    kategorie: '',
    aktenzeichen: '',
    volltext: '',
    gericht_id: '',
    size: 25
  });

  const [selectedGericht, setSelectedGericht] = useState('');

  // Bestimme die Instanz anhand des Gerichts
  const getInstanzFromGericht = (entscheidung) => {
    if (!entscheidung || !entscheidung.gericht_id) return null;
    
    // Hole das Gericht aus der gerichte-Liste anhand der ID
    const gerichtDetails = gerichte.find(g => g.id === entscheidung.gericht_id);
    if (!gerichtDetails) return null;

    switch(gerichtDetails.instanz) {
      case 'BSG':
        return 3;
      case 'LSG':
        return 2;
      case 'SG':
        return 1;
      default:
        return null;
    }
  };

  // Wähle das korrekte Datum basierend auf der Instanz
  const getInstanzDatum = (entscheidung) => {
    debug('getInstanzDatum für Entscheidung:', {
      id: entscheidung.id,
      gericht_id: entscheidung.gericht_id,
      gericht: gerichte.find(g => g.id === entscheidung.gericht_id),
      datum_1_instanz: entscheidung.datum_1_instanz,
      datum_2_instanz: entscheidung.datum_2_instanz,
      datum_3_instanz: entscheidung.datum_3_instanz
    });

    const instanz = getInstanzFromGericht(entscheidung);
    debug('Erkannte Instanz:', instanz);

    // Verwende nur das zur Instanz passende Datum
    switch(instanz) {
      case 1:
        return entscheidung.datum_1_instanz;
      case 2:
        return entscheidung.datum_2_instanz;
      case 3:
        return entscheidung.datum_3_instanz;
      default:
        // Wenn keine Instanz erkannt wurde, nehmen wir das erste verfügbare Datum
        return entscheidung.datum_1_instanz || entscheidung.datum_2_instanz || entscheidung.datum_3_instanz;
    }
  };

  // Instanz-Text für Tooltip
  const getInstanzText = (instanz, gerichtBezeichnung) => {
    switch(instanz) {
      case 1:
        return `Erstinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      case 2:
        return `Zweitinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      case 3:
        return `Letztinstanzliche Entscheidung des ${gerichtBezeichnung}`;
      default:
        return '';
    }
  };

  // Entscheidungen laden mit Override-Parametern
  const fetchEntscheidungenOverride = async ({ effectivePage, effectiveFilter, effectiveOrderBy, effectiveOrder }) => {
    setIsLoading(true);
    try {
      const params = {
        page: effectivePage + 1,  // da API 1-indexed erwartet
        size: effectiveFilter.size,
        search: effectiveFilter.aktenzeichen || undefined,
        volltext: effectiveFilter.volltext || undefined,
        gericht_id: effectiveFilter.gericht_id || undefined,
        sachgebiet: effectiveFilter.sachgebiet || undefined,
        kategorie: effectiveFilter.kategorie || undefined,
        sort_by: effectiveOrderBy,
        sort_order: effectiveOrder
      };
      debug('Sende Anfrage (Override) mit Parametern:', params);
      const response = await apiClient.get('/public/entscheidungen/', { params });
      debug('API Response Daten:', response.data.items);
      
      // Überprüfe die Daten vor dem Setzen
      const processedData = response.data.items.map(item => {
        debug('Verarbeite Entscheidung:', {
          id: item.id,
          aktenzeichen: item.aktenzeichen,
          datum_1_instanz: item.datum_1_instanz,
          datum_2_instanz: item.datum_2_instanz,
          datum_3_instanz: item.datum_3_instanz,
          gericht_id: item.gericht_id,
          gericht_bezeichnung: item.gericht_bezeichnung
        });
        return item;
      });
      
      setEntscheidungen(processedData);
      setTotalCount(response.data.total);

      debug('Entscheidungen nach Verarbeitung:', processedData);
    } catch (error) {
      console.error('Fehler beim Laden der Entscheidungen (Override):', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialer Load und URL-Parameter-Synchronisierung
  useEffect(() => {
    const initializeFromURL = async () => {
      debug('URL Parameter haben sich geändert', { 
        action: location.action,
        pathname: location.pathname
      });

      const pageParam = searchParams.get('page');
      const sizeParam = searchParams.get('size');
      const gerichtParam = searchParams.get('gericht_id');
      const sachgebietParam = searchParams.get('sachgebiet');
      const kategorieParam = searchParams.get('kategorie');
      const aktenzeichenParam = searchParams.get('aktenzeichen');
      const volltextParam = searchParams.get('volltext');
      const orderByParam = searchParams.get('sort_by');
      const orderParam = searchParams.get('sort_order');

      const effectivePage = pageParam !== null ? parseInt(pageParam) : 0;
      const effectiveSize = sizeParam !== null ? parseInt(sizeParam) : 25;
      const effectiveFilter = {
        gericht_id: gerichtParam || '',
        sachgebiet: sachgebietParam || '',
        kategorie: kategorieParam || '',
        aktenzeichen: aktenzeichenParam || '',
        volltext: volltextParam || '',
        size: effectiveSize
      };
      const effectiveOrderBy = orderByParam !== null ? orderByParam : 'datum_1_instanz';
      const effectiveOrder = orderParam !== null ? orderParam : 'desc';

      // Update der States
      setPage(effectivePage);
      setCurrentPageSize(effectiveSize);
      setFilter(effectiveFilter);
      setOrderBy(effectiveOrderBy);
      setOrder(effectiveOrder);

      // Führe die Suche mit den effektiven Werten aus
      await fetchEntscheidungenOverride({ effectivePage, effectiveFilter, effectiveOrderBy, effectiveOrder });
    };

    const loadGerichteIfNeeded = async () => {
      try {
        if (!gerichte.length) {
          debug('Lade Gerichte');
          const response = await apiClient.get('/gerichte/');
      debug('Gerichte geladen', { count: response.data.length });
      setGerichte(response.data);
        }
    } catch (error) {
      console.error('Fehler beim Laden der Gerichte:', error);
    }
  };

    const init = async () => {
      await loadGerichteIfNeeded();
      // Wenn zurück navigiert wurde (POP) oder initiales Laden noch nicht erfolgt ist, dann initialisieren:
      if (location.action === 'POP' || !initialLoadDoneRef.current) {
        await initializeFromURL();
        initialLoadDoneRef.current = true;
      }
    };

    init();
  }, [location, searchParams, gerichte.length]);

  // Update URL wenn sich Filter ändern
  const updateURL = (updates) => {
    // Wenn die Änderung durch Navigation ausgelöst wurde, keine neue URL setzen
    if (isNavigatingRef.current) return;
    
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
  };

  // Filter-Größenänderung (oben) - Nur Filter ändern für nächste Suche
  const handleFilterSizeChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    debug('Filter Size Change Handler', { newSize });
    setFilter(prev => ({
      ...prev,
      size: newSize
    }));
    updateURL({ size: newSize.toString() });
  };

  // Einträge pro Seite ändern (unten) - Mit sofortigem Reload
  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    debug('Rows Per Page Change Handler', { newSize });
    setCurrentPageSize(newSize);
    setFilter(prev => ({
      ...prev,
      size: newSize
    }));
    setPage(0);
    updateURL({ 
      size: newSize.toString(),
      page: '0'
    });
    // Übergebe den aktualisierten Filter direkt mit dem neuen size-Wert
    const updatedFilter = {
      ...filter,
      size: newSize
    };
    fetchEntscheidungenOverride({ 
      effectivePage: 0, 
      effectiveFilter: updatedFilter, 
      effectiveOrderBy: orderBy, 
      effectiveOrder: order 
    });
  };

  // Suche ausführen
  const handleSearch = () => {
    debug('Suche wird ausgeführt');
    fetchEntscheidungenOverride({ effectivePage: page, effectiveFilter: filter, effectiveOrderBy: orderBy, effectiveOrder: order });
  };

  const handleReset = () => {
    debug('Filter werden zurückgesetzt');
    const defaultFilter = {
      sachgebiet: '',
      kategorie: '',
      aktenzeichen: '',
      volltext: '',
      gericht_id: '',
      size: 25
    };
    
    setFilter(defaultFilter);
    setSelectedGericht('');
    setCurrentPageSize(25);
    setPage(0);
    setOrderBy('datum_1_instanz');
    setOrder('desc');
    
    // Lösche alle Such-Parameter aus der URL
    setSearchParams(new URLSearchParams());
  };

  // Filter-Änderungen
  const handleFilterChange = (field) => (event) => {
    debug('Filter Change Handler', { field, value: event.target?.value });
    const newValue = event.target?.value;
    
    // Bei Volltextsuche die Sortierung auf Relevanz umstellen
    if (field === 'volltext' && newValue) {
      setOrderBy('rank');
      setOrder('asc');
      updateURL({ 
        [field]: newValue,
        sort_by: 'rank',
        sort_order: 'asc',
        page: '0'
      });
    } else if (field === 'volltext' && !newValue) {
      // Wenn Volltextsuche geleert wird, zurück zur Datumssortierung
      setOrderBy('datum_1_instanz');
      setOrder('desc');
      updateURL({ 
        [field]: newValue,
        sort_by: 'datum_1_instanz',
        sort_order: 'desc',
        page: '0'
      });
    } else {
      updateURL({ [field]: newValue, page: '0' });
    }

    setFilter(prev => ({
      ...prev,
      [field]: newValue
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    debug('Page Change Handler', { newPage });
    setPage(newPage);
    updateURL({ page: newPage.toString() });
    fetchEntscheidungenOverride({ effectivePage: newPage, effectiveFilter: filter, effectiveOrderBy: orderBy, effectiveOrder: order });
  };

  // Sortierungsfunktion
  const handleRequestSort = (property) => {
    debug('Sort Request Handler', { 
      currentProperty: orderBy, 
      newProperty: property, 
      currentOrder: order 
    });

    const newOrder = orderBy === property && order === 'asc' ? 'desc' : 'asc';
    setOrderBy(property);
    setOrder(newOrder);
    updateURL({ 
      sort_by: property,
      sort_order: newOrder
    });
    fetchEntscheidungenOverride({ effectivePage: page, effectiveFilter: filter, effectiveOrderBy: property, effectiveOrder: newOrder });
  };

  // Sortiere die Daten
  const sortedData = entscheidungen;

  // Sortier-Header-Komponente
  const SortLabel = ({ property, label }) => {
    const isActive = orderBy === property;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            opacity: 0.7
          }
        }}
        onClick={() => handleRequestSort(property)}
      >
        {label}
        <Box sx={{ ml: 0.5, display: 'flex', flexDirection: 'column', height: '16px' }}>
          <Box
            component="span"
            sx={{
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderBottom: '4px solid',
              opacity: isActive && order === 'asc' ? 1 : 0.3,
              mb: '2px'
            }}
          />
          <Box
            component="span"
            sx={{
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid',
              opacity: isActive && order === 'desc' ? 1 : 0.3
            }}
          />
        </Box>
      </Box>
    );
  };

  // Entscheidung Details laden
  const handleRowClick = (event, id) => {
    // Wenn es ein mittlerer Mausklick war, lassen wir das Standard-Verhalten zu
    if (event.button !== 1) {
      event.preventDefault();
      navigate(`/entscheidungen/${id}`, { state: { entscheidung: null } });
    }
  };

  const handleGerichtChange = (event, newValue) => {
    const value = newValue ? newValue.id : '';
    setSelectedGericht(value);
    setFilter(prev => ({
      ...prev,
      gericht_id: value
    }));
    updateURL({ gericht_id: value });
  };

  return (
    <Box sx={{ 
      width: '70%', 
      margin: '0 auto',
      p: 3
    }}>
      <Typography variant="h4" gutterBottom>
        Entscheidungen
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Volltextsuche"
              variant="outlined"
              value={filter.volltext}
              onChange={handleFilterChange('volltext')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Aktenzeichen"
              variant="outlined"
              value={filter.aktenzeichen}
              onChange={handleFilterChange('aktenzeichen')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              value={gerichte.find(g => g.id === selectedGericht) || null}
              onChange={handleGerichtChange}
              options={gerichte}
              getOptionLabel={(option) => option.bezeichnung || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Gericht"
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography noWrap>
                    {option.bezeichnung}
                  </Typography>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sachgebiet</InputLabel>
              <Select
                value={filter.sachgebiet}
                onChange={handleFilterChange('sachgebiet')}
                label="Sachgebiet"
              >
                <MenuItem value="">Alle</MenuItem>
                {sachgebiete.map((sg) => (
                  <MenuItem key={sg.code} value={sg.code}>
                    {sg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Kategorie</InputLabel>
              <Select
                value={filter.kategorie}
                onChange={handleFilterChange('kategorie')}
                label="Kategorie"
              >
                <MenuItem value="">Alle</MenuItem>
                {kategorien.map((kat) => (
                  <MenuItem key={kat.code} value={kat.code}>
                    {kat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Ergebnisse pro Seite</InputLabel>
              <Select
                value={filter.size}
                onChange={handleFilterSizeChange}
                label="Ergebnisse pro Seite"
              >
                {limitOptions.map((limit) => (
                  <MenuItem key={limit} value={limit}>
                    {limit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{ mt: 1 }}
            >
              Zurücksetzen
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              sx={{ mt: 1 }}
            >
              Suchen
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ position: 'relative', minHeight: '200px' }}>
        {isLoading && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ padding: '6px 6px 6px 16px', width: '48px' }}></TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px', width: '48px' }}>Instanz</TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <SortLabel property="aktenzeichen" label="Aktenzeichen" />
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <SortLabel property="datum_1_instanz" label="Datum" />
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <SortLabel property="gericht_bezeichnung" label="Gericht" />
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <SortLabel property="sachgebiet" label="Sachgebiet" />
              </TableCell>
              <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                <SortLabel property="kategorie" label="Kategorie" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((entscheidung) => (
              <TableRow 
                key={entscheidung.id}
                hover
                component={Link}
                to={`/entscheidungen/${entscheidung.id}`}
                onClick={(e) => handleRowClick(e, entscheidung.id)}
                sx={{ 
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    textDecoration: 'none'
                  }
                }}
              >
                <TableCell sx={{ padding: '6px 6px 6px 16px', width: '48px' }}>
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/entscheidungen/${entscheidung.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)' 
                      } 
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px', width: '48px' }}>
                  {(() => {
                    const instanz = getInstanzFromGericht(entscheidung);
                    if (instanz) {
                      return (
                        <Tooltip 
                          title={getInstanzText(instanz, entscheidung.gericht_bezeichnung)}
                          arrow
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: INSTANZ_COLORS[instanz],
                              color: 'white',
                              borderRadius: '12px',
                              padding: '0px 12px',
                              minWidth: '32px',
                              height: '24px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              cursor: 'help'
                            }}
                          >
                            {INSTANZ_LABELS[instanz]}
                          </Box>
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                  {entscheidung.aktenzeichen}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                  {(() => {
                    const datum = getInstanzDatum(entscheidung);
                    return datum ? new Date(datum).toLocaleDateString('de-DE') : '-';
                  })()}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>
                  {entscheidung.gericht_bezeichnung || '-'}
                </TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>{entscheidung.sachgebiet_name || '-'}</TableCell>
                <TableCell sx={{ padding: '6px 6px 6px 16px' }}>{entscheidung.kategorie_name || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={currentPageSize}  // Verwende die aktuelle Seitengröße
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Einträge pro Seite"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count}`
          }
        />
      </TableContainer>
    </Box>
  );
};

export default Entscheidungen; 